import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { Box, Button, Divider, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { evaluate, round } from "mathjs";
import {Accordion, AccordionSummary, AccordionDetails,Grid } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormWrapper from "../common/FormWrapper";
import FormField from "../common/FormField";
import { fetchSections, pusblishTemplate, sectionAutoSave, setFormData, submitSection} from "../../slices/FbSections";
import { SectionSchema } from "../../utils/schemas/initial";
import { notify } from "../../../../bmr/utils/methods/notification";
import DeleteModal from "../../../../bmr/components/common/BaseModal/DeleteModal";
import { fetchTemplates,  updateTemplateStatus} from "../../../../bmr/slices/formTemplate";
import dayjs from "dayjs";

const DynamicFormGenerator = ({
  formJson,
  onSubmit,
  loading,
  formData,
  setDFormData,
}) => {
const [expandedIndex, setExpandedIndex] = useState(0);

  const fieldTypeMap = {
    General: "text",
    Note: "textarea",
    Validations: "text",
    Label: "label",
    Toggle: "switch",
    "Input Radio": "radio-group",
    "Input Dropdown": "select",
    "Input Text": "text",
    "Input Number": "number",
    "Static Number": "number",
    "Input Email": "email",
    "Input Current Time": "time",
    "Input Date": "date",
    "Input Date and Time": "datetime",
    "Linked Field": "linked-field",
    Hyperlink: "hyperlink",
    "File Upload": "file",
    "Image Upload": "image_upload",
    Barcode: "barcode",
  };

  const layoutOptions = {
    12: { gridSize: 12, cols: 1 },
    6: { gridSize: 6, cols: 2 },
    4: { gridSize: 4, cols: 3 },
    3: { gridSize: 3, cols: 4 },
  };

  const processSections = (sections) => {
    return sections.map((section) => {
      const sortedFields = [...section.fields].sort(
        (a, b) => a.order - b.order
      );

      const fieldGroups = [];
      let currentGroup = [];
      let currentLayout = null;

      sortedFields.forEach((field) => {
        const fieldLayout = field.property.field_layout || 12;

        if (currentLayout !== fieldLayout) {
          if (currentGroup.length > 0) {
            fieldGroups.push({
              layout: currentLayout,
              fields: currentGroup,
            });
          }
          currentGroup = [];
          currentLayout = fieldLayout;
        }

        currentGroup.push({
          ...field,
          type: fieldTypeMap[field.property.field_type] || "text",
          required: field.property.general?.required || false,
        });
      });

      if (currentGroup.length > 0) {
        fieldGroups.push({
          layout: currentLayout,
          fields: currentGroup,
        });
      }

      return {
        ...section,
        fieldGroups,
      };
    });
  };

  const formSections = processSections(formJson);

  const getFieldOptions = (fieldUuid) => {
  for (const section of formJson) {
    for (const field of section.fields) {
      if (field.input_uuid === fieldUuid) {
        const opts = field?.property?.options?.options || [];
        return opts.map((opt) => ({
          value: opt.option_uuid,
          label: opt.label,
        }));
      }
    }
  }
  return [];
};


  const handleFieldChange = (fieldUuid, value) => {
    setDFormData((prev) => {
      const updatedSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          field.input_uuid === fieldUuid ? { ...field, value } : field
        ),
      }));

      return { ...prev, sections: updatedSections };
    });
  };

  const getHiddenFields = () => {
    const hidden = [];

    if (!formData?.sections?.length) return hidden;
    const allFields = formData.sections.flatMap((s) => s.fields || []);
    const fieldsWithConditions = allFields.filter(
      (f) => f.property?.conditions?.length
    );
    fieldsWithConditions.forEach((fieldItem) => {
      const selectedValue = allFields.find(
        (f) => f.input_uuid === fieldItem.input_uuid
      )?.value;
      fieldItem.property.conditions.forEach((cond) => {
        if (cond.fbt_option_uuid !== selectedValue) {
          hidden.push(cond.target_input_field_uuid);
        }
      });
    });

    return hidden;
  };

  const hiddenFields = getHiddenFields();

  const allOptionsDict = useMemo(() => {
  const dict = {};
  formJson.forEach(section => {
    section.fields.forEach(field => {
      field?.property?.options?.options?.forEach(opt => {
        dict[opt.option_uuid] = opt.label;
      });
    });
  });
  return dict;
}, [formJson]);

const handleAccordionChange = (index) => (event, isExpanded) => {
  setExpandedIndex(isExpanded ? index : null);
};

  return (
    <FormWrapper onSubmit={onSubmit} loading={loading} className="p-3">
      {formSections.map((section, index) => (
        <Box key={section?.section_uuid}    sx={{
        border: `1px solid ${expandedIndex === index ? "#1c5fdaff" : "#E0E0E0"}`,
        borderRadius: "8px",
        mb: 2,
        overflow: "hidden",
      }}>
          <Accordion     expanded={expandedIndex === index}
          onChange={handleAccordionChange(index)}
            elevation={0}
            sx={{
              border: "none",
              margin: "0px",
              "&:before": { display: "none" },
            }}
          >
            {(section?.property?.display_details ||
              section?.property?.display_field_number) && (
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="border-bottom m-0 px-2 expanded-accordian p-[0.5rem]"
                  sx={{
              backgroundColor: `${expandedIndex === index ? "#EEF4FF" : "transparent"}`,
              borderBottom: "1px solid #E0E0E0",
              minHeight: "64px",
              "& .MuiAccordionSummary-content": {
                margin: 0,
                display: "block",
                padding:"0.5rem"
              },
            }}
              >
                <Typography variant="h6" sx={{ fontWeight: "600" }}>
                  {section?.property?.display_details &&
                  section?.property?.display_field_number ? (
                    <>
                      {`${index + 1}. ${section?.name}`}
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            ml: 1,
                            fontWeight: 400,
                            color: "text.secondary",
                          }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : section?.property?.display_field_number ? (
                    `${index + 1}.`
                  ) : section?.property?.display_details ? (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: "600" , color:"#3571FF"}}>
                      {section?.name}
                      </Typography>
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400,fontSize: "0.875rem", color: "text.secondary" }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : null}
                </Typography>
              </AccordionSummary>
            )}

            <AccordionDetails className="p-0 px-2">
              {section.fieldGroups.map((group, groupIndex) => (
                <Grid key={groupIndex} container>
                  {group.fields.map((field) => {
                    if (hiddenFields.includes(field.input_uuid)) {
                      return null;
                    }
                    const fieldValue =
                      formData.sections
                        .flatMap((s) => s.fields)
                        .find((f) => f.input_uuid === field.input_uuid)
                        ?.value ||
                      field.value ||
                      "";

                    const showFormula = field?.property?.calculation
                      ?.display_formula
                      ? JSON.parse(
                          field?.property?.calculation?.display_formula
                        )
                      : false;
                    const calulationProp = field?.property?.calculation;
                    return (
                      <Grid
                        item
                        key={field.input_uuid}
                        xs={12}
                        sm={layoutOptions[group.layout]?.gridSize || 12}
                      >
                        <Box sx={{ paddingTop: "1rem", padding:1 }}>
                          {
                            field.type === "label" ? (
                              <Typography variant="body" className="text-sm font-bold">{field?.label}</Typography>

                            ) : (
                              <Typography
                                  variant="subtitle1"
                                  className="inline-block font-normal"
                                  sx={{ paddingBottom: "0.625rem" }}
                                >
                                  {field?.label}{" "}
                                  {calulationProp?.unit_of_measurement
                                    ? `(${calulationProp?.unit_of_measurement})`
                                    : ""}
                                {field?.property?.general?.required && (
                                  <Box
                                    component="span"
                                    className="text-red-500"
                                    sx={{ ml: 0.5 }}
                                  >
                                    *
                                  </Box>
                                )}
                              </Typography>
                            )
                          }
                    {/* @info bind up with the label may remove */}
                          {/* <Typography variant="subtitle1">
                            {calulationProp?.unit_of_measurement
                              ? `(${calulationProp?.unit_of_measurement})`
                              : ""}
                          </Typography> */}
                          {field.property.field_type === "Calculation" ? (
                            <>
                              <FormField
                                name={field.input_uuid}
                                type="number"
                                label=""
                                fullWidth
                                value={fieldValue}
                                disabled
                                size="small"
                              />
                              {showFormula && calulationProp?.formula && (
                                <Typography variant="caption">
                                  Formula: {field?.parsedFormula}
                                </Typography>
                              )}
                            </>
                          ) :
                          field.property.field_type === "Linked Field" ? (
                            <>
                              <FormField
                                name={field.input_uuid}
                                label=""
                                fullWidth
                                value={allOptionsDict[fieldValue] || fieldValue || field.property.linked_field?.linked_field || ""}
                                disabled
                                size="small"
                              />
                            </>) :
                            (
                              <FormField
                                // name={field.input_uuid}
                                name={`sections.${[index]}.fields.${[0]}.${field?.label}`}
                                type={field.type}
                                options={
                                  field.type === "radio-group" || field.type === "select"
                                    ? getFieldOptions(field.input_uuid)
                                    : []
                                }
                                row="row"
                                noMargin={true}
                                placeholder={
                                  field.placeholder ||
                                  `Enter ${field.label?.toLowerCase() || "value"}`
                                }
                                hasBothLabels={field.type === "Toggle"}
                                label1={field?.property?.toggle?.toggle_msg_1}
                                label2={field?.property?.toggle?.toggle_msg_2}

                                onChange={(input) => {
                                  let rawValue;

                                  // Detect value type
                                  if (typeof input === "object" && input !== null && input.$d) {
                                    rawValue = input; // Date object from MUI pickers
                                  } else if (input?.target) {
                                    rawValue = input.target.value; // Normal input event
                                  } else {
                                    rawValue = input; // Direct value (e.g. toggle, select, radio)
                                  }

                                  let finalValue = rawValue;

                                  switch (field.property.field_type) {

                                    case "Input Date":
                                      finalValue = rawValue ? dayjs(rawValue).format("YYYY-MM-DD") : "";
                                      break;

                                    case "Input Current Time":
                                      finalValue = rawValue || "";
                                      break;

                                    case "Input Date and Time":
                                      finalValue = rawValue ? dayjs(rawValue).toISOString() : "";
                                      break;

                                    default:
                                      // keep rawValue
                                      finalValue = rawValue;
                                      break;
                                  }

                                  handleFieldChange(field.input_uuid, finalValue);
                                }}
                                format={field.property.field_type === "Input Date" ? field?.property?.input_date?.date_display_format : 'DD/MM/YY'}
                                timeFormat={field.property.field_type === "Input Current Time" ? field?.property?.input_current_time?.time_format : 'hh:mm A'}

                                combineDate={field.property.field_type === "Input Date and Time" ? field?.property?.input_date_and_time?.date_display_format : 'DD/MM/YY'}
                                combineTime={field.property.field_type === "Input Date and Time" ? field?.property?.input_date_and_time?.time_format : 'HH:mm'}

                                linkLabel={field?.property?.hyperlink?.name}
                                linkUrl={field?.property?.hyperlink?.url}
                                defaultValue={field.property.field_type === "Input Text" ? field?.property?.input_text?.default_val : ''}
                              />)}                        
                        </Box>
                        <Typography
                          variant="body"
                          className="px-2"
                          sx={{ mb: 1, color: "#FB9D2C"}}
                        >
                          {field?.property?.note?.message_note
                            ? `Note : ${field?.property?.note?.message_note}`
                            : ""}
                        </Typography>
                        {/* <Divider sx={{ mb: "0.9rem" }} /> */}
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      ))}
    </FormWrapper>
  );
};

const DynamicPreview = ({ handleCloseDrawer, cardId, getValues }) => {
  const { id } = useParams();
  const location = useLocation();
  const [loading,setLoading] = useState(false)
  
  const FORM_SCHEMA_UPDATED = useSelector(
    (state) => state.bmrFbSections.formData.sections
  );

  const { fbTemplate } = useSelector((state) => state.bmrFbl);
  let templateName = fbTemplate.items.find(
    (name) => name?.id == (id ?? cardId)
  );

  const dispatch = useDispatch();
  const [formData, setDynamicFormData] = useState({
    sections: FORM_SCHEMA_UPDATED || [],
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleSubmit = () => {
    dispatch(submitSection(id ?? cardId));
  };

  const handlePublish = async () => {
    try {
      const response = await dispatch(
        pusblishTemplate(id ? id : cardId)
      ).unwrap();
      if (!location.pathname.includes("preview")) {
        handleCloseDrawer(); //@info close the drawer if user is in template list page
      }
      dispatch(updateTemplateStatus({ id, status: response.data.status }));
      dispatch(fetchTemplates());
    } catch (error) {
      notify.error(error?.message || error?.data?.message);
    }
  };

  // Update local state when Redux state changes
  useEffect(() => {
    if (FORM_SCHEMA_UPDATED) {
      setDynamicFormData({ sections: FORM_SCHEMA_UPDATED });
    }
  }, [FORM_SCHEMA_UPDATED]);

  /*The below code block can be removed when autosave starts working fine or 
    we provide a button to save manually before previewing */
  /* Code block start */
  useEffect(() => {
    fetchSetFormData();
  }, []);

  const fetchSetFormData = async () => {
    try {
      setLoading(true)
      if (location.pathname === (`/bmr/form-management/${id}`)) {
        const currentFormData = getValues('sections');
        await dispatch(sectionAutoSave({ data: {sections:currentFormData}, id }));
      }
      const response = await dispatch(fetchSections(id? id: cardId)).unwrap();

      const modifiedSections =
        response.data.sections.length > 0
          ? injectDefaultValues(response.data.sections)
          : [SectionSchema];
      dispatch(setFormData({ sections: modifiedSections }));
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  /* Code block ends */

  const getParsedFormula = (formVariables, formula) => {
    let parsedFormula = formula;
    Object.entries(formVariables).forEach(([key, value]) => {
      const regex = new RegExp(`\\b${key}\\b`, "g");
      parsedFormula = parsedFormula.replace(regex, value);
    });
    return parsedFormula;
  };

  const injectDefaultValues = (sections) => {
    return sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        const hasValue =
          field.value !== undefined &&
          field.value !== null &&
          field.value !== "";
        const defaultValue = field?.property?.linked_field?.default_val;

        return {
          ...field,
          value: hasValue ? field.value : defaultValue ?? "",
        };
      }),
    }));
  };

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const inputValues = {};
    const fieldInputLabels = {};
    const updatedFields = [];

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value, property } = field;
        const { field_type } = property || {};
        fieldInputLabels[input_uuid] = field.label;
        inputValues[input_uuid] =
          field_type === "Input Number" && value
            ? parseFloat(value) ?? 0
            : value;
      }
    }

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value: currentValue, property } = field;
        const {
          field_type,
          calculation,
          input_calculation_variables,
          linked_field,
        } = property || {};

        if (field_type === "Calculation" && calculation?.formula) {
          const variables = {};
          const formVariables = {};

          for (const variable of input_calculation_variables || []) {
            formVariables[variable.name] =
              fieldInputLabels[variable.linked_field_uuid];
            variables[variable.name] =
              inputValues[variable.linked_field_uuid] ||
              (!isNaN(variable.default_val) &&
                parseFloat(variable.default_val)) ||
              0;
          }

          try {
            let result = round(
              evaluate(calculation.formula, variables),
              calculation.decimal_places || 4
            );
            if (calculation.decimal_places != null) {
              result = parseFloat(
                Number(result).toFixed(calculation.decimal_places)
              );
            }
            const resultStr = result?.toString() ?? "";

            if (currentValue !== resultStr) {
              updatedFields.push({
                fieldUuid: input_uuid,
                value: resultStr,
                parsedFormula: getParsedFormula(
                  formVariables,
                  calculation.formula
                ),
              });
            }
          } catch (err) {
            console.warn(
              `Invalid formula for field "${input_uuid}": ${err.message}`
            );
          }
        }

        if (field_type === "Linked Field" && linked_field?.linked_field) {
          const newValue = inputValues[linked_field.linked_field];

          updatedFields.push({
            fieldUuid: input_uuid,
            value:
              newValue !== undefined && newValue !== null && newValue !== ""
                ? newValue
                : linked_field.default_val || "",
          });
        }

      }
    }

    if (!updatedFields.length) return;

    setDynamicFormData((prev) => {
      const updatesMap = Object.fromEntries(
        updatedFields.map(({ fieldUuid, value, parsedFormula }) => [
          fieldUuid,
          { value, parsedFormula },
        ])
      );

      const newSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const newValue = updatesMap[field.input_uuid]?.value;
          if (newValue !== undefined && field.value !== newValue) {
            return {
              ...field,
              value: newValue,
              parsedFormula: updatesMap[field.input_uuid]?.parsedFormula,
            };
          }
          return field;
        }),
      }));

      const isSame =
        JSON.stringify(newSections) === JSON.stringify(prev.sections);
      if (isSame) return prev;

      return {
        ...prev,
        sections: newSections,
      };
    });
  }, [formData]);

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: 4,
         borderBottom: "1px solid #E4F9F2",
          backgroundColor: "#E4F9F2",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
         Template Name: {templateName?.name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              onClick={handlePublish}
              sx={{ borderRadius: "0.375rem !important", width: "7.18rem !important"}}
              disabled={templateName?.status===2}
            >
              Publish
            </Button>
          <IconButton onClick={handleCloseDrawer} sx={{color:'black'}}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      {loading ? (
        <Box className="p-3">
          <Skeleton variant="text" width="30%" height={20} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
        </Box>
      ) : (
        <Box className="p-4">
        <DynamicFormGenerator
          formJson={formData.sections}
          formData={formData}
          setDFormData={setDynamicFormData}
          loading={false}
          />
          </Box>
      )}

      <DeleteModal
        open={openConfirmation}
        setClose={handleCloseConfirmation}
        title="Template is not submitted yet, would you like to submit"
        buttonText="Submit"
        handleSuccess={handleSubmit}
        handleRejection={handleCloseConfirmation}
        rejectButtonText="Cancel"
      />
    </div>
  );
};

export default DynamicPreview;
